/** Hub marketing panel — sample report rows per service (not live API shape). */

export type ImeiHubPreviewServiceId =
  | 'verify'
  | 'passport'
  | 'lock'
  | 'carrier'
  | 'safecheck'
  | 'business'
  | 'android'
  | 'gsx'
  | 'tmobile'

export type HubPreviewSectionTone = 'ok' | 'warn' | 'risk' | 'unknown' | 'muted'

export type HubPreviewReportSection = {
  id: string
  titleKey: string
  detailKey: string
  tone: HubPreviewSectionTone
}

type PreviewPayload = {
  brand?: string | null
  model?: string | null
  carrier?: string | null
  device?: {
    color?: string | null
    release_year?: number | null
    tac?: string | null
  } | null
}

/** Marketing samples — mixed outcomes so hub previews feel realistic (not all OK). */
const ROWS: Record<ImeiHubPreviewServiceId, readonly Omit<HubPreviewReportSection, 'id'>[]> = {
  verify: [
    {
      titleKey: 'vc.imeiHub.services.verify.check4',
      detailKey: 'vc.imeiHub.preview.verify.d4unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.verify.check3',
      detailKey: 'vc.imeiHub.preview.verify.d3warn',
      tone: 'warn',
    },
    {
      titleKey: 'vc.imeiHub.services.verify.check2',
      detailKey: 'vc.imeiHub.services.verify.check2Detail',
      tone: 'ok',
    },
    {
      titleKey: 'vc.imeiHub.services.verify.check1',
      detailKey: 'vc.imeiHub.services.verify.check1Detail',
      tone: 'warn',
    },
  ],
  passport: [
    {
      titleKey: 'vc.imeiHub.services.passport.check4',
      detailKey: 'vc.imeiHub.preview.passport.d4unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.passport.check2',
      detailKey: 'vc.imeiHub.preview.passport.d2warn',
      tone: 'warn',
    },
    {
      titleKey: 'vc.imeiHub.services.passport.check1',
      detailKey: 'vc.imeiHub.preview.passport.d1',
      tone: 'ok',
    },
    {
      titleKey: 'vc.imeiHub.services.passport.check3',
      detailKey: 'vc.imeiHub.preview.passport.d3warn',
      tone: 'warn',
    },
  ],
  lock: [
    {
      titleKey: 'vc.imeiHub.services.lock.check1',
      detailKey: 'vc.imeiHub.preview.lock.d1unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.lock.check2',
      detailKey: 'vc.imeiHub.preview.lock.d2risk',
      tone: 'risk',
    },
    {
      titleKey: 'vc.imeiHub.services.lock.check4',
      detailKey: 'vc.imeiHub.preview.lock.d4',
      tone: 'ok',
    },
    {
      titleKey: 'vc.imeiHub.services.lock.check3',
      detailKey: 'vc.imeiHub.preview.lock.d3warn',
      tone: 'warn',
    },
  ],
  carrier: [
    {
      titleKey: 'vc.imeiHub.services.carrier.check3',
      detailKey: 'vc.imeiHub.preview.carrier.d3warn',
      tone: 'warn',
    },
    {
      titleKey: 'vc.imeiHub.services.carrier.check2',
      detailKey: 'vc.imeiHub.preview.carrier.d2unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.carrier.check1',
      detailKey: 'vc.imeiHub.preview.carrier.d1',
      tone: 'ok',
    },
  ],
  safecheck: [
    {
      titleKey: 'vc.imeiHub.services.safecheck.check3',
      detailKey: 'vc.imeiHub.preview.safecheck.d3unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.safecheck.check1',
      detailKey: 'vc.imeiHub.preview.safecheck.d1warn',
      tone: 'warn',
    },
    {
      titleKey: 'vc.imeiHub.services.safecheck.check2',
      detailKey: 'vc.imeiHub.preview.safecheck.d2',
      tone: 'ok',
    },
  ],
  business: [
    {
      titleKey: 'vc.imeiHub.services.business.check2',
      detailKey: 'vc.imeiHub.preview.business.d2unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.business.check4',
      detailKey: 'vc.imeiHub.preview.business.d4warn',
      tone: 'warn',
    },
    {
      titleKey: 'vc.imeiHub.services.business.check1',
      detailKey: 'vc.imeiHub.preview.business.d1',
      tone: 'ok',
    },
    {
      titleKey: 'vc.imeiHub.services.business.check3',
      detailKey: 'vc.imeiHub.preview.business.d3warn',
      tone: 'warn',
    },
  ],
  android: [
    {
      titleKey: 'vc.imeiHub.services.android.check3',
      detailKey: 'vc.imeiHub.preview.android.d3unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.android.check1',
      detailKey: 'vc.imeiHub.preview.android.d1warn',
      tone: 'warn',
    },
    {
      titleKey: 'vc.imeiHub.services.android.check4',
      detailKey: 'vc.imeiHub.preview.android.d4',
      tone: 'ok',
    },
    {
      titleKey: 'vc.imeiHub.services.android.check2',
      detailKey: 'vc.imeiHub.preview.android.d2warn',
      tone: 'warn',
    },
  ],
  gsx: [
    {
      titleKey: 'vc.imeiHub.services.gsx.check4',
      detailKey: 'vc.imeiHub.preview.gsx.d4unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.gsx.check2',
      detailKey: 'vc.imeiHub.preview.gsx.d2warn',
      tone: 'warn',
    },
    {
      titleKey: 'vc.imeiHub.services.gsx.check1',
      detailKey: 'vc.imeiHub.preview.gsx.d1',
      tone: 'ok',
    },
    {
      titleKey: 'vc.imeiHub.services.gsx.check3',
      detailKey: 'vc.imeiHub.preview.gsx.d3warn',
      tone: 'warn',
    },
  ],
  tmobile: [
    {
      titleKey: 'vc.imeiHub.services.tmobile.check3',
      detailKey: 'vc.imeiHub.preview.tmobile.d3unknown',
      tone: 'unknown',
    },
    {
      titleKey: 'vc.imeiHub.services.tmobile.check2',
      detailKey: 'vc.imeiHub.preview.tmobile.d2warn',
      tone: 'warn',
    },
    {
      titleKey: 'vc.imeiHub.services.tmobile.check1',
      detailKey: 'vc.imeiHub.preview.tmobile.d1',
      tone: 'ok',
    },
  ],
}

/** Replace {{brand}}, {{model}}, {{carrier}}, {{color}}, {{year}}, {{tac}} in preview detail strings. */
export function interpolateHubPreviewDetail(template: string, data: PreviewPayload): string {
  const brand = data.brand?.trim() || 'Apple'
  const model = data.model?.trim() || 'iPhone 14'
  const carrier = data.carrier?.trim() || 'Tele2'
  const color = data.device?.color?.trim() || 'Midnight'
  const year = data.device?.release_year != null ? String(data.device.release_year) : '2022'
  const tac = data.device?.tac?.trim() || '35678901'
  const vars: Record<string, string> = {
    '{{brand}}': brand,
    '{{model}}': model,
    '{{carrier}}': carrier,
    '{{color}}': color,
    '{{year}}': year,
    '{{tac}}': tac,
  }
  let out = template
  for (const [token, value] of Object.entries(vars)) {
    out = out.split(token).join(value)
  }
  return out
}

export function buildImeiHubPreviewSections(
  service: ImeiHubPreviewServiceId,
): HubPreviewReportSection[] {
  const rows = ROWS[service] ?? []
  return rows.map((row, index) => ({
    id: `hub-preview-${service}-${index}`,
    ...row,
  }))
}
